// Authentication store using Zustand

import { create } from 'zustand';
import type { SafeUser, LoginInput } from '@ultisim/shared';
import { authApi, ApiError } from '@/api';

interface AuthState {
  user: SafeUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (data: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  login: async (data: LoginInput) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authApi.login(data);

      // Store access token
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('tokenExpiry', String(response.expiresAt));

      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Login failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('tokenExpiry');
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  loadUser: async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      set({ isLoading: false });
      return;
    }

    try {
      // Check if token is expired
      const expiry = localStorage.getItem('tokenExpiry');
      const now = Math.floor(Date.now() / 1000);

      if (expiry && parseInt(expiry) < now) {
        // Token expired, try to refresh
        try {
          const refreshResponse = await authApi.refresh();
          localStorage.setItem('accessToken', refreshResponse.accessToken);
          localStorage.setItem('tokenExpiry', String(refreshResponse.expiresAt));
        } catch {
          // Refresh failed, clear auth
          localStorage.removeItem('accessToken');
          localStorage.removeItem('tokenExpiry');
          set({ isLoading: false });
          return;
        }
      }

      // Load user data
      const user = await authApi.getMe();
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('tokenExpiry');
      set({ isLoading: false });
    }
  },

  clearError: () => set({ error: null }),
}));

// Auto-refresh token before expiry
setInterval(async () => {
  const expiry = localStorage.getItem('tokenExpiry');
  const now = Math.floor(Date.now() / 1000);

  // Refresh 5 minutes before expiry
  if (expiry && parseInt(expiry) - now < 300) {
    try {
      const response = await authApi.refresh();
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('tokenExpiry', String(response.expiresAt));
    } catch {
      // If refresh fails, trigger logout
      useAuthStore.getState().logout();
    }
  }
}, 60000); // Check every minute

export default useAuthStore;
